import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Checkbox } from '../ui/checkbox'
import { Calendar, Download, CheckCircle2 } from 'lucide-react'
import { CalendarService } from '../../services/calendarServices'
import { isOutlookConfigured, outlookErrorMessage } from '../../services/outlookCalendar'
import { calendarDateRange, localDateString, newCalendarEntries } from '../../lib/calendarDates'
import { timeEntriesService } from '../../services/database'
import { useAuth } from '../../contexts/AuthContext'
import { ProviderLogo } from '../ui/provider-logo'

function initialDates(currentWeekStart) {
  const start = currentWeekStart || new Date()
  const end = new Date(start)
  end.setDate(end.getDate() + 6)
  return { start: localDateString(start), end: localDateString(end) }
}

export default function CalendarImportDialog({ onImportComplete, currentWeekStart }) {
  const { user } = useAuth()
  const service = useMemo(() => new CalendarService(user?.id), [user?.id])
  const mounted = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])
  const [isOpen, setIsOpen] = useState(false)
  const [step, setStep] = useState('connect')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [googleConnected, setGoogleConnected] = useState(false)
  const [outlookAccount, setOutlookAccount] = useState(null)
  const [outlookCalendars, setOutlookCalendars] = useState([])
  const [outlookCalendarId, setOutlookCalendarId] = useState('')
  const [dateRange, setDateRange] = useState(() => initialDates(currentWeekStart))
  const [selectedSources, setSelectedSources] = useState(['google', 'outlook'])
  const [previewEntries, setPreviewEntries] = useState([])
  const [selectedEntries, setSelectedEntries] = useState(new Set())
  const [skippedCount, setSkippedCount] = useState(0)
  const [importedCount, setImportedCount] = useState(0)
  const outlookConnected = !!outlookAccount

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    setLoading(true)
    service.initialize().then(async () => {
      if (cancelled) return
      setGoogleConnected(service.googleService.isAuthenticated)
      setOutlookAccount(service.outlookService.account || null)
      if (service.outlookService.isAuthenticated) {
        const calendars = await service.outlookService.getCalendars()
        if (!cancelled) setOutlookCalendars(calendars)
      }
    }).catch(error => {
      if (!cancelled) setError(outlookErrorMessage(error))
    }).finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [isOpen, service])

  const connectOutlook = async () => {
    setLoading(true)
    setError('')
    try {
      const account = await service.outlookService.authenticate()
      setOutlookAccount(account)
      setOutlookCalendarId('')
      setOutlookCalendars([])
      setSelectedSources(sources => [...new Set([...sources, 'outlook'])])
      setOutlookCalendars(await service.outlookService.getCalendars())
    } catch (error) { setError(outlookErrorMessage(error)) }
    finally { setLoading(false) }
  }

  const disconnectOutlook = async () => {
    setLoading(true)
    setError('')
    try { await service.outlookService.disconnect() }
    catch (error) { setError(outlookErrorMessage(error)) }
    finally {
      setOutlookAccount(null)
      setOutlookCalendars([])
      setOutlookCalendarId('')
      setLoading(false)
    }
  }

  const connectGoogle = async () => {
    setLoading(true)
    setError('')
    try {
      await service.googleService.authenticate()
      setGoogleConnected(true)
      setSelectedSources(sources => [...new Set([...sources, 'google'])])
    } catch (error) { setError(`Could not connect to Google Calendar: ${error.message}`) }
    finally { setLoading(false) }
  }

  const previewImport = async () => {
    setLoading(true)
    setError('')
    try {
      const { startDate, endDate } = calendarDateRange(dateRange.start, dateRange.end)
      const sources = selectedSources.filter(source => source === 'google' ? googleConnected : outlookConnected)
      if (!sources.length) throw new Error('Connect and select at least one calendar to import.')
      const entries = await service.importEvents(startDate, endDate, sources, outlookCalendarId)
      if (!mounted.current) return
      const existing = await timeEntriesService.list('date')
      if (!mounted.current) return
      const fresh = newCalendarEntries(entries, existing)
      setSkippedCount(entries.length - fresh.length)
      setPreviewEntries(fresh)
      setSelectedEntries(new Set(fresh.map((_, index) => index)))
      setStep('select')
    } catch (error) { setError(error.message) }
    finally { setLoading(false) }
  }

  const importSelected = async () => {
    setLoading(true)
    setError('')
    try {
      // Recheck before saving, including after a partially successful import/retry.
      const existing = await timeEntriesService.list('date')
      if (!mounted.current) return
      const selected = previewEntries.filter((_, index) => selectedEntries.has(index))
      const entries = newCalendarEntries(selected, existing)
      if (entries.length) await timeEntriesService.createBulk(entries)
      setImportedCount(entries.length)
      setSkippedCount(count => count + selected.length - entries.length)
      setStep('success')
      onImportComplete?.(entries.length)
    } catch (error) { setError(`Could not import events: ${error.message}`) }
    finally { setLoading(false) }
  }

  const toggleSource = (source, checked) => setSelectedSources(sources => checked
    ? [...new Set([...sources, source])] : sources.filter(item => item !== source))
  const toggleEntry = index => setSelectedEntries(selected => {
    const next = new Set(selected)
    if (next.has(index)) next.delete(index)
    else next.add(index)
    return next
  })

  const openDialog = () => {
    setStep('connect')
    setError('')
    setDateRange(initialDates(currentWeekStart))
    setPreviewEntries([])
    setSelectedEntries(new Set())
    setSkippedCount(0)
    setIsOpen(true)
  }

  return <>
    <Button variant="secondary" onClick={openDialog}><Calendar size={15} />Import from calendar</Button>
    <Dialog open={isOpen} onOpenChange={open => { if (!loading) setIsOpen(open) }}>
      <DialogContent aria-labelledby="calendar-import-title" aria-busy={loading} style={{ width: 'min(760px, 94vw)', maxHeight: '88vh', overflowY: 'auto' }}>
        <DialogHeader>
          <DialogTitle><span id="calendar-import-title">Import calendar events</span></DialogTitle>
          <DialogDescription>Connect a calendar, review your events, and save selected meetings to Time Audit.</DialogDescription>
        </DialogHeader>
        {error && <div role="alert" style={{ padding: 12, color: 'var(--danger)', background: 'var(--bg-inset)', borderRadius: 8, marginBottom: 16 }}>{error}</div>}
        {loading && <p role="status" style={{ color: 'var(--fg-muted)' }}>Working… Complete the Microsoft sign-in window if it is open.</p>}

        {step === 'connect' && <div style={{ display: 'grid', gap: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 12 }}>
            <section aria-label="Outlook Calendar" style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 8 }}>
              <h3 style={{ margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 8 }}><ProviderLogo provider="outlook" size={20} />Outlook Calendar</h3>
              {outlookConnected ? <>
                <Badge variant="success">Connected</Badge>
                <p style={{ overflowWrap: 'anywhere', fontSize: 13 }}>{outlookAccount.username || outlookAccount.name}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <Button variant="secondary" size="sm" disabled={loading} onClick={connectOutlook}>Reconnect Outlook</Button>
                  <Button variant="secondary" size="sm" disabled={loading} onClick={connectOutlook}>Switch Microsoft account</Button>
                  <Button variant="ghost" size="sm" disabled={loading} onClick={disconnectOutlook}>Disconnect Outlook</Button>
                </div>
              </> : <>
                <p style={{ fontSize: 13, color: 'var(--fg-muted)' }}>Use your work, school, or personal Microsoft account.</p>
                <Button variant="primary" disabled={loading || !isOutlookConfigured()} onClick={connectOutlook}>Connect Outlook</Button>
                {!isOutlookConfigured() && <p style={{ fontSize: 13, color: 'var(--fg-muted)' }}>Outlook is not set up yet. Ask your SPCC administrator to enable the connection.</p>}
              </>}
            </section>
            <section aria-label="Google Calendar" style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 8 }}>
              <h3 style={{ margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 8 }}><ProviderLogo provider="google" size={20} />Google Calendar</h3>
              <p style={{ fontSize: 13, color: 'var(--fg-muted)' }}>Import meetings from your Google calendar.</p>
              {googleConnected ? <>
                <Badge variant="success">Connected</Badge>
                <Button variant="ghost" size="sm" disabled={loading} onClick={() => { service.googleService.disconnect(); setGoogleConnected(false) }}>Disconnect Google</Button>
              </> : <Button variant="secondary" disabled={loading} onClick={connectGoogle}>Connect Google</Button>}
            </section>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--fg-muted)' }}>SPCC reads your calendar to suggest time entries. Only selected entries are saved in your workspace. All-day, cancelled, and declined Outlook events are excluded.</p>
          {(googleConnected || outlookConnected) && <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <div><Label htmlFor="calendar-start-date">Start date</Label><Input id="calendar-start-date" type="date" disabled={loading} value={dateRange.start} onChange={e => setDateRange(range => ({ ...range, start: e.target.value }))} /></div>
              <div><Label htmlFor="calendar-end-date">End date</Label><Input id="calendar-end-date" type="date" disabled={loading} min={dateRange.start} value={dateRange.end} onChange={e => setDateRange(range => ({ ...range, end: e.target.value }))} /></div>
            </div>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--fg-muted)' }}>Dates and times use your device timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone}).</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              {outlookConnected && <label style={{ display: 'flex', gap: 8 }}><Checkbox disabled={loading} checked={selectedSources.includes('outlook')} onCheckedChange={checked => toggleSource('outlook', checked)} />Outlook Calendar</label>}
              {googleConnected && <label style={{ display: 'flex', gap: 8 }}><Checkbox disabled={loading} checked={selectedSources.includes('google')} onCheckedChange={checked => toggleSource('google', checked)} />Google Calendar</label>}
            </div>
            {outlookConnected && selectedSources.includes('outlook') && <div>
              <Label htmlFor="outlook-calendar">Outlook calendar</Label>
              <select id="outlook-calendar" value={outlookCalendarId} disabled={loading} onChange={e => setOutlookCalendarId(e.target.value)} style={{ width: '100%', padding: 10, color: 'var(--fg)', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 6 }}>
                <option value="">Default calendar</option>
                {outlookCalendars.filter(calendar => !calendar.isDefaultCalendar).map(calendar => <option key={calendar.id} value={calendar.id}>{calendar.name}</option>)}
              </select>
            </div>}
          </>}
        </div>}

        {step === 'select' && <div style={{ display: 'grid', gap: 12 }}>
          <p style={{ margin: 0 }}>{previewEntries.length} new events · {selectedEntries.size} selected{skippedCount > 0 && ` · ${skippedCount} already imported`}</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" size="sm" disabled={loading} onClick={() => setSelectedEntries(new Set(previewEntries.map((_, index) => index)))}>Select all</Button>
            <Button variant="ghost" size="sm" disabled={loading} onClick={() => setSelectedEntries(new Set())}>Clear all</Button>
          </div>
          {!previewEntries.length && <p>No new timed events in this date range. Try another calendar or date range.</p>}
          <div style={{ display: 'grid', gap: 8, maxHeight: '42vh', overflowY: 'auto' }}>
            {previewEntries.map((entry, index) => <label key={`${entry.source}:${entry.external_id || index}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: 12, border: '1px solid var(--border)', borderRadius: 8 }}>
              <Checkbox disabled={loading} aria-label={`Import ${entry.activity_title}`} checked={selectedEntries.has(index)} onCheckedChange={() => toggleEntry(index)} />
              <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                <strong>{entry.activity_title}</strong>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--fg-muted)', marginTop: 4 }}>{new Date(`${entry.date}T00:00:00`).toLocaleDateString()} · {entry.notes} · {entry.duration_minutes} min</span>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--fg-muted)', marginTop: 4 }}>{entry.source === 'outlook_calendar' ? 'Outlook' : 'Google'} · {entry.category} · {entry.strategic_value} strategic value</span>
              </span>
            </label>)}
          </div>
        </div>}

        {step === 'success' && <div role="status" style={{ padding: '24px 0', textAlign: 'center' }}>
          <CheckCircle2 size={32} color="var(--success)" />
          <h3>{importedCount} calendar {importedCount === 1 ? 'event imported' : 'events imported'}</h3>
          <p>Your selected meetings are saved in Time Audit.</p>
          {skippedCount > 0 && <p>{skippedCount} already imported events skipped.</p>}
        </div>}

        <DialogFooter style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <Button variant="ghost" disabled={loading} onClick={() => setIsOpen(false)}>{step === 'success' ? 'Done' : 'Close'}</Button>
          {step === 'connect' && <Button variant="primary" disabled={loading || !((outlookConnected && selectedSources.includes('outlook')) || (googleConnected && selectedSources.includes('google')))} onClick={previewImport}><Download size={15} />Preview import</Button>}
          {step === 'select' && <>
            <Button variant="secondary" disabled={loading} onClick={() => { setError(''); setStep('connect') }}>Back to settings</Button>
            <Button variant="primary" disabled={loading || !selectedEntries.size} onClick={importSelected}>Import {selectedEntries.size} events</Button>
          </>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
}
